/**
 * Standalone public chat widget — no auth, no app chrome.
 * Route: /widget/:agentId
 * Talks to /api/widget/* endpoints.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Bot, Loader2, X, RefreshCw } from "lucide-react";
import { API_BASE } from "@/lib/api";

const VISITOR_KEY_PREFIX = "sas:widget:visitor:";
const CONV_KEY_PREFIX = "sas:widget:conv:";

export default function Widget() {
  const { agentId } = useParams();
  const [config, setConfig] = useState(null);
  const [configErr, setConfigErr] = useState(null);
  const [convId, setConvId] = useState(null);
  const [visitorId, setVisitorId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showPrompts, setShowPrompts] = useState(true);
  const [ended, setEnded] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Load config
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/widget/agents/${agentId}/config`);
        if (!res.ok) throw new Error(res.status === 404 ? "Agent not available" : `HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setConfig(data);
      } catch (err) {
        if (!cancelled) setConfigErr(err.message || "Failed to load agent");
      }
    })();
    return () => { cancelled = true; };
  }, [agentId]);

  // Start (or resume) conversation when config loads
  useEffect(() => {
    if (!config) return;
    const vKey = VISITOR_KEY_PREFIX + agentId;
    const cKey = CONV_KEY_PREFIX + agentId;
    const existingVisitor = localStorage.getItem(vKey);
    const existingConv = localStorage.getItem(cKey);

    const start = async () => {
      try {
        const res = await fetch(`${API_BASE}/widget/agents/${agentId}/conversations`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            visitor_id: existingVisitor || null,
            source_url: document.referrer || window.location.href,
          }),
        });
        if (!res.ok) throw new Error("Could not start conversation");
        const data = await res.json();
        setConvId(data.conversation_id);
        setVisitorId(data.visitor_id);
        localStorage.setItem(vKey, data.visitor_id);
        localStorage.setItem(cKey, data.conversation_id);
        // Seed welcome
        setMessages([{ id: "welcome", role: "assistant", content: data.welcome_message || config.welcome_message }]);
      } catch (err) {
        setConfigErr(err.message);
      }
    };

    // Try resume by listing messages for existing conversation
    if (existingConv) {
      (async () => {
        const res = await fetch(`${API_BASE}/widget/agents/${agentId}/conversations/${existingConv}/messages`);
        if (res.ok) {
          const msgs = await res.json();
          if (msgs && msgs.length > 0) {
            setConvId(existingConv);
            setVisitorId(existingVisitor);
            setMessages([
              { id: "welcome", role: "assistant", content: config.welcome_message },
              ...msgs.map((m) => ({ id: m.id, role: m.role, content: m.content })),
            ]);
            return;
          }
        }
        start();
      })();
    } else {
      start();
    }
  }, [config, agentId]);

  // Scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = useCallback(async (content) => {
    const text = (content || input).trim();
    if (!text || sending || !convId) return;
    setInput("");
    setShowPrompts(false);
    const userMsg = { id: `u-${Date.now()}`, role: "user", content: text };
    setMessages((arr) => [...arr, userMsg, { id: `a-${Date.now()}`, role: "assistant", content: "", streaming: true }]);
    setSending(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API_BASE}/widget/agents/${agentId}/conversations/${convId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: text }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6);
          if (!json) continue;
          try {
            const evt = JSON.parse(json);
            if (evt.type === "token") {
              setMessages((arr) => {
                const next = [...arr];
                const last = next[next.length - 1];
                if (last && last.streaming) last.content += evt.content;
                return next;
              });
            }
            if (evt.type === "done") {
              setMessages((arr) => {
                const next = [...arr];
                const last = next[next.length - 1];
                if (last) { delete last.streaming; }
                return next;
              });
            }
            if (evt.type === "error") {
              setMessages((arr) => {
                const next = [...arr];
                const last = next[next.length - 1];
                if (last) { last.content = `Sorry — ${evt.error}`; delete last.streaming; }
                return next;
              });
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setMessages((arr) => {
          const next = [...arr];
          const last = next[next.length - 1];
          if (last) { last.content = `Sorry — ${err.message}`; delete last.streaming; }
          return next;
        });
      }
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }, [input, sending, convId, agentId]);

  const endChat = async () => {
    if (!convId) return;
    try {
      await fetch(`${API_BASE}/widget/agents/${agentId}/conversations/${convId}/end`, { method: "POST" });
    } catch (_) {}
    setEnded(true);
  };

  const resetChat = () => {
    localStorage.removeItem(CONV_KEY_PREFIX + agentId);
    setConvId(null); setMessages([]); setEnded(false);
    setConfig({ ...config });
  };

  if (configErr) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#050505] p-6 text-center text-white/65" data-testid="widget-error">
        <div>
          <Bot className="mx-auto h-10 w-10 text-white/25" />
          <p className="mt-3 text-sm">{configErr}</p>
        </div>
      </div>
    );
  }
  if (!config) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#050505] text-white/45" data-testid="widget-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const accent = config.accent_color || "#a855f7";
  const bg = config.bg_color || "#0a0a0a";

  return (
    <div
      className="flex h-screen flex-col"
      style={{ background: bg, color: "#fff", fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}
      data-testid="widget-root"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between border-b border-white/5 px-4 py-3"
        style={{ background: `${accent}15` }}
      >
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: accent }}>
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-medium text-white" data-testid="widget-agent-name">{config.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/45">AI Assistant</div>
          </div>
        </div>
        <button onClick={resetChat} className="rounded-lg p-1.5 text-white/45 hover:bg-white/10 hover:text-white" title="Reset chat" data-testid="widget-reset">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="widget-messages">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-md text-white" : "rounded-bl-md border text-white/95"}`}
              style={m.role === "user"
                ? { background: accent }
                : { background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.06)" }}
              data-testid={`widget-msg-${m.role}`}
            >
              {m.content || (m.streaming ? "…" : "")}
              {m.streaming && <span className="ml-0.5 inline-block h-3 w-2 animate-pulse" style={{ background: accent }} />}
            </div>
          </div>
        ))}
        {showPrompts && (config.suggested_prompts || []).length > 0 && messages.length === 1 && (
          <div className="flex flex-wrap gap-2 pt-2" data-testid="widget-prompts">
            {(config.suggested_prompts || []).map((p) => (
              <button key={p} onClick={() => send(p)} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/85 hover:bg-white/10">
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-white/5 p-3" data-testid="widget-input-form">
        {ended ? (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-center text-xs text-emerald-300">
            Conversation ended. Someone will follow up shortly.
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-2 py-1">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={config.status === "inactive" ? "Agent is inactive" : "Type a message…"}
              disabled={sending || config.status === "inactive"}
              className="flex-1 bg-transparent px-2 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none"
              data-testid="widget-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim() || config.status === "inactive"}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-white disabled:opacity-30"
              style={{ background: accent }}
              data-testid="widget-send"
              aria-label="Send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        )}
        <div className="mt-2 flex items-center justify-between text-[10px] text-white/40">
          <span data-testid="widget-visitor-id">Visitor · {visitorId?.slice(0, 10) || "—"}</span>
          {!ended && (
            <button type="button" onClick={endChat} className="hover:text-white/65" data-testid="widget-end">
              End conversation
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
